import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { AlfaSinapsiDevice } from "src/model/class/general classes/alfa-sinapsi-devices";
import { RtuevoSinapsiDevice } from "src/model/class/general classes/rtuevo-sinapsi-device";
import { SmartGateway } from "src/model/class/general classes/smartgateway";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ReadingInterval } from "src/components/shared-components/enum/enumReadingInterval";
import { iReadingInterval } from "src/model/interface/iReadingInterval";
import { RtuevoReadingType } from "src/model/enum/enumRtuevoReadingType";

@Component({
    selector: "app-environment-add-device",
    templateUrl: "./environment-add-device.component.html",
    styleUrls: ["./environment-add-device.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnvironmentAddDeviceComponent implements OnInit {
  public formAddDeviceSinapsi: UntypedFormGroup = null;
  public smartGatewayReadingInterval: Array<iReadingInterval> = [];

  private _defaultPollingRTUEVO = 86400;

  @Input("isOpenRtuevoComponent") isOpenRtuevoSinapsi: boolean;
  @Input("isOpenSinapsiAlfaComponent") isOpenAlfaSinapsi: boolean;
  @Input("isOpenSmartGateway") isOpenSmartGateway: boolean;
  @Input() okText: string;
  @Input() closeText: string;

  constructor(private _fb: UntypedFormBuilder) { }

  ngOnInit(): void {
    this.getArrayReadingInterval();
    this.initializeAddDeviceSinapsi();
  }

  /**
   * Handles the submission of the formAddDeviceSinapsi in the case of Alfa Sinapsi
   * @returns
   */
  public onSubmitAlfaSinapsiDevice(): AlfaSinapsiDevice {
    if (this.formAddDeviceSinapsi.valid) {
      const addedAlfaSinapsiDevice: AlfaSinapsiDevice = new AlfaSinapsiDevice({
        ...this.formAddDeviceSinapsi.getRawValue(),
      });

      return addedAlfaSinapsiDevice;
    }
  }

  /**
   * Handles the submission of the formAddDeviceSinapsi in the case of Rtuevo Sinapsi device
   * @returns
   */
  public onSubmitRtuevoSinapsiDevice(): RtuevoSinapsiDevice {
    if (this.formAddDeviceSinapsi.valid) {
      const addedRtuevoSinapsiDevice: RtuevoSinapsiDevice =
        new RtuevoSinapsiDevice({
          ...this.formAddDeviceSinapsi.getRawValue(),
          rtuevoReadingType:
            this.formAddDeviceSinapsi.getRawValue()["rtuevoReadingType"] ===
              "datapoints"
              ? RtuevoReadingType.DATAPOINTS
              : RtuevoReadingType.MAIN_DATA,
        });

      return addedRtuevoSinapsiDevice;
    }
  }

  /**
   * Handles the submission of the formAddDeviceSinapsi in the case of smartgateway
   * @returns
   */
  public onSubmitSmartGateway(): SmartGateway {
    if (this.formAddDeviceSinapsi.valid) {
      const addedSmartGateway: SmartGateway = new SmartGateway({
        ...this.formAddDeviceSinapsi.getRawValue(),
      });

      return addedSmartGateway;
    }
  }

  /*
   * Checks the conditions required to show error message, for example when the required form field is not filled out
   * @param formControlToCheck the formControlName
   * @returns
   */
  public checkShowErrorMessage(formControlToCheck: string = "") {
    if (
      formControlToCheck === undefined ||
      formControlToCheck === "" ||
      formControlToCheck === null
    ) {
      return (
        this.formAddDeviceSinapsi.touched && this.formAddDeviceSinapsi.invalid
      );
    } else if (this.formAddDeviceSinapsi.get(formControlToCheck)) {
      return (
        this.formAddDeviceSinapsi.get(formControlToCheck).touched &&
        this.formAddDeviceSinapsi.get(formControlToCheck).invalid
      );
    }
  }

  /**
   * Handle the prevention of entering string in a numerical input field
   * @param event keyboard event
   * @returns
   */
  public isInputNumeric(event: KeyboardEvent): boolean {
    const input = event.key;
    return /^\d*\.?\d*$/.test(input);
  }

  /**
   * Initialize the add new Sinapsi device form
   */
  private initializeAddDeviceSinapsi(): void {
    this.checkTypeOfSinapsiDevice();
  }

  /**
   * Check the type of Sinapsi device to add
   */
  private checkTypeOfSinapsiDevice() {
    if (this.isOpenRtuevoSinapsi) {
      this.formAddDeviceSinapsi = this._fb.group({
        rtuevoPollingSeconds: [{ value: this._defaultPollingRTUEVO, disabled: true }, Validators.required],
        rtuevoApiKey: ["", Validators.required],
        rtuevoSerialNumber: ["", Validators.required],
        rtuevoDescription: ["", Validators.required],
        rtuevoReadingType: ["datapoints", Validators.required],
      });
    } else if (this.isOpenAlfaSinapsi) {
      this.formAddDeviceSinapsi = this._fb.group({
        alfaMqttIP: ["", Validators.required],
        alfaMqttPort: ["", Validators.required],
        alfaMqttUsername: ["", Validators.required],
        alfaMqttPassword: ["", Validators.required],
        alfaMqttClientID: ["", Validators.required],
        alfaMqttSSLTLS: [false, Validators.required],
        alfaDescription: ["", Validators.required],
        alfaActivationKey: ["", Validators.required],
      });
    } else {
      this.formAddDeviceSinapsi = this._fb.group({
        description: ["", Validators.required],
        apiKey: ["", Validators.required],
        domain: ["", Validators.required],
        plantID: [0, Validators.required],
        serialNumber: ["", Validators.required],
        smartGatewayReadingInterval: [ReadingInterval.DAILY],
      });
    }
  }

  /**
   * Gets an array of object containing reading intervals key and value
   */
  private getArrayReadingInterval(): void {
    this.smartGatewayReadingInterval = Object.keys(ReadingInterval)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        key: StaticClass.createReadingIntervalLabel(ReadingInterval[key]),
        value: ReadingInterval[key],
      }));
  }
}
